import React, { useCallback, useMemo } from "react";

import MetricsGridView from "./MetricsGridView";

import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";
import { isClickableMetric } from "@/constants/metrics";
import { ICompositeMetric } from "@/stores/compositeMetric";

/**
 *
 */
interface Props {
  shouldShowColorTransferMetric?: boolean;
  metrics: ICompositeMetric[][] | null;
  onMetricClick?: (metric: ICompositeMetric) => void;
}

/**
 * Container component for displaying a grid of composite metrics
 * Handles theme-based styling and optional transfer metric color customization
 * @param root0 - Component props
 * @param root0.shouldShowColorTransferMetric - Whether to show colored styling for transfer value metric
 * @param root0.metrics - 2D array of composite metrics organized by rows and columns
 * @param root0.onMetricClick - Callback when a metric is clicked (for explanations/details)
 */
const MetricsGridContainer = ({
  shouldShowColorTransferMetric = true,
  metrics,
  onMetricClick,
}: Props) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const updatedMetric = useMemo(() => {
    if (shouldShowColorTransferMetric) return metrics;
    // loop and change background, border color transfer metric
    return metrics?.map(metric =>
      metric.map(item => {
        const defaultStyle = {
          background: "transparent",
          border: isDark ? "#3F3F46" : "#D4D4D8",
          text: isDark ? "#FFFFFF" : "#3F3F46",
        };

        return !shouldShowColorTransferMetric && item.label === "Transfer Value"
          ? { ...item, styles: { ...item.styles, ...defaultStyle } }
          : item;
      })
    );
  }, [isDark, metrics, shouldShowColorTransferMetric]);

  const isSmallScreen = useMobileScreen();

  const handleMetricPress = useCallback(
    (metric: ICompositeMetric) => {
      if (onMetricClick && isClickableMetric(metric.label)) {
        onMetricClick(metric);
      }
    },
    [onMetricClick]
  );

  const metricPressHandlers = useMemo(
    () =>
      metrics
        ?.flat()
        .reduce<
          Record<string, () => void>
        >((handlers, metric) => (metric ? { ...handlers, [metric.label]: () => handleMetricPress(metric) } : handlers), {}) ??
      {},
    [metrics, handleMetricPress]
  );

  const getMetricPressHandler = useCallback(
    (metric: ICompositeMetric) =>
      metricPressHandlers[metric.label] ?? (() => {}),
    [metricPressHandlers]
  );

  return (
    <MetricsGridView
      metrics={updatedMetric}
      isSmallScreen={isSmallScreen}
      isDark={isDark}
      createMetricPressHandler={getMetricPressHandler}
    />
  );
};
export default MetricsGridContainer;
